import { useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '@heroui/react';
import { LessonInfo, LessonSteps, LessonVideo } from '@/views/Lesson';
import { ScormPreviewModal } from '@/views/ModuleDetailView';
import { MainLayout } from '@/widgets/layouts/MainLayout';
import { mapLessonVideo } from '@/features/StepVideoUploads';
import {
  useGetLessonByModuleQuery,
  useGetModuleBaseInfoQuery,
  useGetTermsQuery,
  useLazyGetScormPreviewUrlQuery,
} from '@/entities/Courses';
import { useToast } from '@/shared/lib/hooks/useToast';
import { useRefreshOnVideoUrlExpiry } from '@/shared/lib/hooks';
import { routerPath } from '@/shared/config/router';
import { Container } from '@/shared/ui/Container';
import { CourseBreadcrumbs } from '@/shared/ui/CourseBreadcrumbs';
import { SectionLoader } from '@/shared/ui/Loader';
import { Svg } from '@/shared/ui/Svg';
import DownloadIcon from '@/shared/assets/icons/download.svg';
import { PrivateFileLink } from '@/shared/ui/PrivateFileLink';
import type { FileInfo } from '@/shared/types/files';

const ModuleLessonPreviewPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [scormPreviewUrl, setScormPreviewUrl] = useState<string | null>(null);
  const [getScormPreviewUrl, { isFetching: isScormPreviewLoading }] =
    useLazyGetScormPreviewUrlQuery();
  const { courseId, intakeId, groupNumber, termId, moduleId, lessonId } = useParams<{
    courseId: string;
    intakeId: string;
    groupNumber: string;
    termId: string;
    moduleId: string;
    lessonId: string;
  }>();

  const courseIdNum = Number(courseId);
  const intakeIdNum = Number(intakeId);
  const groupNumberNum = Number(groupNumber);
  const termIdNum = Number(termId);
  const moduleIdNum = Number(moduleId);
  const lessonIdNum = Number(lessonId);

  const hasValidParams = Boolean(
    courseId &&
    intakeId &&
    groupNumber &&
    termId &&
    moduleId &&
    lessonId &&
    !Number.isNaN(courseIdNum) &&
    !Number.isNaN(intakeIdNum) &&
    !Number.isNaN(groupNumberNum) &&
    !Number.isNaN(termIdNum) &&
    !Number.isNaN(moduleIdNum) &&
    !Number.isNaN(lessonIdNum),
  );

  const { data: termsData, isLoading: isTermsLoading } = useGetTermsQuery(
    {
      intakeId: intakeIdNum,
    },
    { skip: !hasValidParams },
  );

  const groupId = useMemo(() => {
    const group = termsData?.data?.groups.find((g) => g.number === groupNumberNum);
    return group?.id;
  }, [termsData?.data?.groups, groupNumberNum]);

  const term = useMemo(
    () => termsData?.data?.terms.find((item) => item.id === termIdNum),
    [termsData?.data?.terms, termIdNum],
  );

  const { data: moduleBaseInfo, isLoading: isModuleBaseInfoLoading } = useGetModuleBaseInfoQuery(
    {
      intakeModuleId: moduleIdNum,
      groupId: groupId || 0,
    },
    { skip: !hasValidParams || !groupId },
  );

  const {
    data: lessonResponse,
    isLoading: isLessonLoading,
    refetch: refetchLesson,
  } = useGetLessonByModuleQuery(
    {
      intakeModuleId: moduleIdNum,
      lessonId: lessonIdNum,
      groupId: groupId || 0,
    },
    { skip: !hasValidParams || !groupId },
  );

  const lesson = lessonResponse?.data?.lesson ?? null;
  const isStepsMode = lesson?.contentMode === 'steps';
  // Signed Stream links expire: the lesson is requested again before they stop working
  useRefreshOnVideoUrlExpiry(
    [
      mapLessonVideo(lesson?.video)?.urlExpiresAt,
      ...(lesson?.steps ?? []).map((step) => mapLessonVideo(step.video)?.urlExpiresAt),
    ],
    refetchLesson,
  );

  const handlePreviewScorm = async (packageId: number) => {
    try {
      const result = await getScormPreviewUrl(packageId).unwrap();
      let url = result?.data?.launchUrl;
      if (!url) return;
      if (url.startsWith('/') && !url.startsWith('//')) {
        url = `${import.meta.env.VITE_API_URL_BASE}${url}`;
      }
      setScormPreviewUrl(url);
    } catch {
      toast({ title: 'Error', description: 'Failed to load SCORM preview', color: 'danger' });
    }
  };
  const lessonVideo = (lesson?.video ?? null) as Record<string, string | null | undefined> | null;
  const embedUrl = lessonVideo?.embedUrl || '';
  const videoUrl = lessonVideo?.url || lessonVideo?.playbackUrl || lessonVideo?.hls || '';
  const moduleTitle = moduleBaseInfo?.data?.module?.title || '';
  const courseTitle = moduleBaseInfo?.data?.course?.title || termsData?.data?.course?.title || '';
  const intakeTitle = moduleBaseInfo?.data?.intake?.title || termsData?.data?.intake?.title || '';
  const termNumber = moduleBaseInfo?.data?.term?.number ?? term?.number;
  const assignmentHref = routerPath.assignmentDetail
    .replace(':courseId', String(courseIdNum))
    .replace(':intakeId', String(intakeIdNum));
  const materialFiles: FileInfo[] =
    lesson?.attachments?.length || isStepsMode
      ? (lesson?.attachments ?? [])
      : lesson?.assignment?.attachments || [];

  const groupDetailPath = routerPath.groupDetail
    .replace(':courseId', String(courseIdNum))
    .replace(':intakeId', String(intakeIdNum))
    .replace(':groupNumber', String(groupNumberNum));
  const termDetailPath = routerPath.termDetail
    .replace(':courseId', String(courseIdNum))
    .replace(':intakeId', String(intakeIdNum))
    .replace(':groupNumber', String(groupNumberNum))
    .replace(':termId', String(termIdNum));
  const moduleDetailPath = routerPath.moduleDetail
    .replace(':courseId', String(courseIdNum))
    .replace(':intakeId', String(intakeIdNum))
    .replace(':groupNumber', String(groupNumberNum))
    .replace(':termId', String(termIdNum))
    .replace(':moduleId', String(moduleIdNum));

  const breadcrumbItems = [
    { label: 'Courses', href: routerPath.courses },
    {
      label: courseTitle || 'Course',
      href: routerPath.courseDetail.replace(':id', String(courseIdNum)),
    },
    {
      label: intakeTitle ? `${intakeTitle} - Group ${groupNumberNum}` : `Group ${groupNumberNum}`,
      href: groupDetailPath,
    },
    { label: `Term ${termNumber ?? '—'}`, href: termDetailPath },
    { label: moduleTitle || 'Module', href: moduleDetailPath },
    { label: `Session ${lesson?.order ?? '—'}` },
  ];

  if (isTermsLoading || isModuleBaseInfoLoading || isLessonLoading) {
    return (
      <MainLayout headerTitle="Material Preview">
        <SectionLoader />
      </MainLayout>
    );
  }

  if (!hasValidParams || !lesson) {
    return null;
  }

  return (
    <MainLayout headerTitle="Material Preview">
      <Container className="flex flex-col gap-4">
        <CourseBreadcrumbs items={breadcrumbItems} />

        <div className="flex items-center justify-between rounded-[16px] bg-white p-4">
          <div className="flex flex-col">
            <p className="text-xs text-grey-secondary">Preview mode</p>
            <h2 className="text-2xl font-medium">Student lesson view</h2>
          </div>
          <Button className="bg-[#1E1E1E] text-white" onPress={() => navigate(-1)}>
            Exit preview
          </Button>
        </div>

        <LessonInfo
          module={moduleTitle || 'Module'}
          numberLesson={lesson.order}
          title={lesson.title}
          scheduledAt={lesson.scheduledAt ?? null}
          endDate={lesson.endDate ?? null}
          moduleTeacher={null}
          lecturer={null}
        />

        {isStepsMode ? (
          <LessonSteps
            steps={lesson.steps ?? []}
            onPreviewScorm={handlePreviewScorm}
            isScormPreviewLoading={isScormPreviewLoading}
          />
        ) : embedUrl ? (
          <LessonVideo
            video={
              {
                embedUrl,
                thumbnailUrl: lessonVideo?.thumbnailUrl || null,
                name: lesson.title,
              } as never
            }
          />
        ) : videoUrl ? (
          <div className="relative w-full overflow-hidden rounded-2xl bg-black">
            <video controls className="aspect-video w-full" src={videoUrl} />
          </div>
        ) : (
          <div className="rounded-[12px] border border-grey-border bg-white p-6 text-grey-secondary">
            Video is unavailable for this lesson.
          </div>
        )}

        <div className="rounded-[20px] bg-white p-6">
          <div className="mb-4 inline-flex items-center rounded-full bg-[#8B0000] px-3 py-1 text-xs text-white">
            Materials
          </div>
          {lesson.description ? (
            <p className="text-sm text-grey-secondary mb-4">{lesson.description}</p>
          ) : null}
          <div className="flex items-end justify-between gap-4">
            <div className="flex flex-col gap-2 w-full max-w-[480px]">
              {materialFiles.length > 0 ? (
                materialFiles.map((file: FileInfo) => (
                  <PrivateFileLink
                    key={file.key}
                    file={file}
                    action="download"
                    className="flex items-center justify-between rounded-[10px] bg-[#F4F4F5] px-4 py-3"
                  >
                    <span className="truncate text-sm text-black">{file.name}</span>
                    <Svg Icon={DownloadIcon} className="text-[#211D55] shrink-0" />
                  </PrivateFileLink>
                ))
              ) : (
                <p className="text-sm text-grey-secondary">No materials attached.</p>
              )}
            </div>
            <Link to={assignmentHref}>
              <Button radius="full" className="bg-[#211D55] text-white">
                Go to assignment
              </Button>
            </Link>
          </div>
        </div>
      </Container>
      <ScormPreviewModal
        isOpen={scormPreviewUrl !== null}
        onOpenChange={(open) => {
          if (!open) setScormPreviewUrl(null);
        }}
        previewUrl={scormPreviewUrl}
      />
    </MainLayout>
  );
};

export default ModuleLessonPreviewPage;
